import React, { useState, useEffect, useCallback, useRef } from "react";
import { colors, font, displayName, formatCurrency } from "../utils/theme.js";
import { api } from "../api/client.js";

// Account Owners dashboard (spec docs/superpowers/specs/2026-10-23-dashboard-design.md). Read-only:
// one GET /api/dashboard per period; every rule is computed server-side (api/src/model/dashboard.js).
// `onOpen(hash, page)` navigates to a client page / task (App sets the hash and switches page).
const PERIODS = [["12m", "Last 12 months"], ["6m", "Last 6 months"], ["ytd", "Year to date"], ["all", "All time"]];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDate = (d) => (d ? `${MONTHS[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}` : "—");
const longDate = (d) => (d ? `${shortDate(d)}, ${d.slice(0, 4)}` : "—");
const money = (v) => (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : formatCurrency(v));

const card = { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: "16px 18px", minWidth: 0 };
const h2 = { margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.8 };
const row = { display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: `1px solid ${colors.border}`, fontSize: 13 };
const linkBtn = { background: "none", border: "none", padding: 0, color: colors.accent, fontWeight: 600, fontSize: 13, fontFamily: font, cursor: "pointer", textAlign: "left" };
const th = { padding: "8px 6px", fontSize: 11, fontWeight: 700, color: colors.textDim, borderBottom: `1px solid ${colors.borderLight}`, textAlign: "center" };
const td = { padding: "8px 6px", borderBottom: `1px solid ${colors.border}`, textAlign: "center", fontVariantNumeric: "tabular-nums" };

function Pills({ options, value, onChange, label }) {
  return (
    <div role="group" aria-label={label} style={{ display: "flex", gap: 4, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 9, padding: 3, flexWrap: "wrap" }}>
      {options.map(([id, text]) => (
        <button key={id} type="button" aria-pressed={value === id} onClick={() => onChange(id)} style={{
          fontFamily: font, fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 7, cursor: "pointer", border: "none",
          background: value === id ? colors.accentDim : "transparent", color: value === id ? colors.accent : colors.textMuted,
        }}>{text}</button>
      ))}
    </div>
  );
}

function ListPanel({ title, children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1100, display: "flex", justifyContent: "flex-end" }}>
      <div role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()} style={{ width: "min(520px, 100%)", height: "100%", overflow: "auto", background: colors.surface, borderLeft: `1px solid ${colors.border}`, padding: 22, boxSizing: "border-box" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16, color: colors.text }}>{title}</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: colors.textDim, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function DashboardPage({ users, onOpen }) {
  const [period, setPeriod] = useState("12m");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [taskMode, setTaskMode] = useState("company");
  const [panel, setPanel] = useState(null); // { title, kind: "proposals"|"tasks", items }

  // Only the latest request may update the page, so a slow response for a period the user has
  // already switched away from never shows under the newly selected button.
  const latest = useRef(0);
  const load = useCallback(async (p) => {
    const id = ++latest.current;
    setError(null);
    try { const d = await api.dashboard.get(p); if (id === latest.current) setData(d); }
    catch (e) { if (id === latest.current) setError(e.message || "error"); }
  }, []);
  useEffect(() => { load(period); }, [period, load]);

  const uName = (id) => { if (!id) return "—"; const u = (users || []).find((x) => x.id === id || x.email === id); return u ? displayName(u) : id; };
  const openClient = (clientId, projectId) => clientId && onOpen?.(`client=${encodeURIComponent(clientId)}${projectId ? `&project=${encodeURIComponent(projectId)}` : ""}`, "clients");
  // A proposal with a client links to that client's page; one without a client is plain text.
  const ProposalTitle = ({ p }) => (p.clientId
    ? <button type="button" style={linkBtn} onClick={() => openClient(p.clientId)}>{p.topic}</button>
    : <span style={{ fontWeight: 600, color: colors.text }}>{p.topic}</span>);
  const openTask = (t) => (t.clientId ? onOpen?.(`client=${encodeURIComponent(t.clientId)}&task=${encodeURIComponent(t.id)}`, "clients") : onOpen?.(`tasks&task=${encodeURIComponent(t.id)}`, "employees"));

  if (error) return (
    <div style={{ padding: 40 }}>
      <div style={{ color: colors.danger, marginBottom: 12 }}>Couldn’t load the dashboard. Check your connection and try again.</div>
      <button onClick={() => load(period)} style={{ padding: "9px 18px", background: colors.accent, border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, fontFamily: font, cursor: "pointer" }}>Retry</button>
    </div>
  );
  if (!data) return <div style={{ padding: 40, color: colors.textMuted }}>Loading dashboard…</div>;

  const h = data.headline;
  const kpis = [
    ["Active pipeline", money(h.activePipeline.value), `${h.activePipeline.count} proposals in progress`, colors.text],
    ["Submitted, awaiting", money(h.submitted.value), `${h.submitted.count} proposals`, colors.text],
    ["Win rate", h.winRate.pct == null ? "—" : `${h.winRate.pct}%`, h.winRate.decided ? `${h.winRate.won} won of ${h.winRate.decided} decided` : "nothing decided yet", colors.success],
    ["Due in 14 days", String(h.dueIn14), "proposal deadlines", colors.warning],
    ["Overdue tasks", String(h.overdueTasks), "open tasks past due", colors.danger],
  ];
  const stageLabel = Object.fromEntries(data.stageTable.stages.map((s) => [s.value, s.label]));
  const proposalsPanel = (title, items) => setPanel({ title, kind: "proposals", items });
  const maxValue = Math.max(1, ...data.pipelineValue.map((v) => v.value));
  const taskRows = taskMode === "company" ? data.tasks.byCompany : data.tasks.byEmployee;
  const taskTotals = taskMode === "company" ? data.tasks.totals.byCompany : data.tasks.totals.byEmployee;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* App renders the page title; this row only holds the period switch. */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Pills label="Proposal period" options={PERIODS} value={period} onChange={setPeriod} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
        {kpis.map(([label, value, sub, color]) => (
          <div key={label} style={{ ...card, padding: "14px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.textDim, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 6, color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16 }}>
        <section style={card}>
          <h2 style={h2}>Clients needing attention</h2>
          {data.attention.length === 0 && <div style={{ fontSize: 13, color: colors.textDim }}>Nothing flagged.</div>}
          {data.attention.map((a) => (
            <div key={a.clientId} style={{ ...row, flexWrap: "wrap" }}>
              <button type="button" style={linkBtn} onClick={() => openClient(a.clientId)}>{a.client}</button>
              <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{a.reasons.map((r) => (
                <span key={r} style={{ fontSize: 11.5, fontWeight: 700, color: /past-due/.test(r) ? colors.danger : colors.warning, border: `1px solid currentColor`, borderRadius: 10, padding: "1px 8px", whiteSpace: "nowrap" }}>{r}</span>
              ))}</span>
            </div>
          ))}
        </section>
        <section style={card}>
          <h2 style={h2}>Proposal deadlines · next 14 days</h2>
          {data.deadlines.length === 0 && <div style={{ fontSize: 13, color: colors.textDim }}>No proposals due in the next 14 days.</div>}
          {data.deadlines.map((d) => (
            <div key={d.id} style={{ ...row, display: "grid", gridTemplateColumns: "60px minmax(0,1fr) auto" }}>
              <span style={{ fontWeight: 700, color: d.dueDate === data.today ? colors.danger : colors.text }}>{shortDate(d.dueDate)}</span>
              <span style={{ minWidth: 0 }}><ProposalTitle p={d} /><span style={{ color: colors.textMuted }}> · {d.client}</span></span>
              <span style={{ fontSize: 11.5, color: colors.textMuted, whiteSpace: "nowrap" }}>{stageLabel[d.stage]}</span>
            </div>
          ))}
        </section>
        <section style={card}>
          <h2 style={h2}>Waiting on a decision</h2>
          {data.waiting.length === 0 && <div style={{ fontSize: 13, color: colors.textDim }}>No submitted proposals past their expected decision date.</div>}
          {data.waiting.slice(0, 8).map((w) => (
            <div key={w.id} style={{ ...row, display: "block" }}>
              <div><ProposalTitle p={w} /><span style={{ color: colors.textMuted }}> · {w.client}</span></div>
              <div style={{ fontSize: 12, color: colors.warning, marginTop: 2 }}>Decision expected {shortDate(w.estimatedResponseDate)} · {w.daysPast} days past{w.submissionDate ? ` · submitted ${shortDate(w.submissionDate)}` : ""}</div>
            </div>
          ))}
          {data.waiting.length > 8 && <button type="button" style={{ ...linkBtn, marginTop: 8 }} onClick={() => proposalsPanel("Waiting on a decision", data.waiting)}>Show all {data.waiting.length}</button>}
        </section>
      </div>

      <section style={card}>
        <h2 style={h2}>Proposals by client and stage</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 980, fontSize: 13, color: colors.text }}>
            <thead><tr>
              <th scope="col" style={{ ...th, textAlign: "left", padding: "8px 10px" }}>Client</th>
              {data.stageTable.stages.map((s, i) => (
                <th key={s.value} scope="col" style={{ ...th, color: s.outcome ? (s.value === "not_selected" ? colors.danger : s.value === "not_pursuing" ? colors.textDim : colors.success) : colors.textDim, borderLeft: i === 7 ? `2px solid ${colors.borderLight}` : "none", lineHeight: 1.25 }}>{s.label}</th>
              ))}
              <th scope="col" style={{ ...th, color: colors.text, borderLeft: `1px solid ${colors.borderLight}` }}>Total</th>
            </tr></thead>
            <tbody>
              {data.stageTable.rows.map((r) => (
                <tr key={r.clientId || "none"}>
                  <th scope="row" style={{ ...td, textAlign: "left", padding: "8px 10px", fontWeight: 600 }}>{r.client}</th>
                  {data.stageTable.stages.map((s, i) => {
                    const n = r.counts[s.value];
                    return (
                      <td key={s.value} style={{ ...td, borderLeft: i === 7 ? `2px solid ${colors.borderLight}` : "none" }}>
                        {n ? <button type="button" aria-label={`${r.client} ${s.value}: ${n}`} style={{ ...linkBtn, fontWeight: 500 }}
                          onClick={() => proposalsPanel(`${r.client} · ${s.label}`, r.proposals.filter((p) => p.stage === s.value))}>{n}</button>
                          : <span style={{ color: colors.textDim }}>·</span>}
                      </td>
                    );
                  })}
                  <td style={{ ...td, fontWeight: 700, borderLeft: `1px solid ${colors.borderLight}` }}>
                    <button type="button" aria-label={`${r.client} total: ${r.total}`} style={{ ...linkBtn, color: colors.text }} onClick={() => proposalsPanel(`${r.client} · all proposals`, r.proposals)}>{r.total}</button>
                  </td>
                </tr>
              ))}
              <tr style={{ background: colors.surfaceAlt }}>
                <th scope="row" style={{ ...td, textAlign: "left", padding: "9px 10px" }}>Total</th>
                {data.stageTable.stages.map((s, i) => <td key={s.value} style={{ ...td, fontWeight: 700, borderLeft: i === 7 ? `2px solid ${colors.borderLight}` : "none" }}>{data.stageTable.totals.counts[s.value]}</td>)}
                <td style={{ ...td, fontWeight: 700, borderLeft: `1px solid ${colors.borderLight}` }}>{data.stageTable.totals.total}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>Counted by due date, else submission date, else the date added. Click a number to see those proposals.</div>
      </section>

      <section style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <h2 style={{ ...h2, margin: 0 }}>Pipeline value by stage</h2>
          <span style={{ fontSize: 12.5, color: colors.textMuted }}>Win rate <b style={{ color: colors.success }}>{h.winRate.pct == null ? "—" : `${h.winRate.pct}%`}</b>{h.winRate.decided ? ` · ${h.winRate.won} won of ${h.winRate.decided} decided` : ""}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {data.pipelineValue.map((v) => (
            <div key={v.stage} style={{ display: "grid", gridTemplateColumns: "170px minmax(0,1fr) 80px", gap: 12, alignItems: "center", fontSize: 12.5 }}>
              <span style={{ color: colors.textMuted }}>{v.label}</span>
              <span style={{ height: 12, background: colors.bg, borderRadius: 3, overflow: "hidden" }}><span style={{ display: "block", height: 12, width: `${Math.round((v.value / maxValue) * 100)}%`, background: v.stage === "submitted" ? "#60a5fa" : colors.accent, borderRadius: 3 }} /></span>
              <span style={{ textAlign: "right", fontWeight: 700, color: colors.text, fontVariantNumeric: "tabular-nums" }}>{money(v.value)}</span>
            </div>
          ))}
        </div>
      </section>

      <section style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <h2 style={{ ...h2, margin: 0 }}>Tasks</h2>
          <Pills label="Group tasks by" options={[["company", "By company"], ["employee", "By employee"]]} value={taskMode} onChange={setTaskMode} />
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 520, fontSize: 13, color: colors.text }}>
            <thead><tr>
              <th scope="col" style={{ ...th, textAlign: "left", padding: "8px 10px" }}>{taskMode === "company" ? "Company" : "Employee"}</th>
              <th scope="col" style={{ ...th, color: colors.danger }}>Overdue</th>
              <th scope="col" style={th}>Due next 7 days</th>
              <th scope="col" style={{ ...th, color: colors.success }}>Completed last 7 days</th>
            </tr></thead>
            <tbody>
              {taskRows.map((r) => (
                <tr key={r.key}>
                  <th scope="row" style={{ ...td, textAlign: "left", padding: "8px 10px" }}>
                    <button type="button" aria-label={`${r.label} tasks`} style={linkBtn} onClick={() => setPanel({ title: `${r.label} · tasks`, kind: "tasks", items: r.tasks })}>{r.label}</button>
                  </th>
                  <td style={{ ...td, fontWeight: 700, color: r.overdue ? colors.danger : colors.textDim }}>{r.overdue}</td>
                  <td style={td}>{r.dueNext7}</td>
                  <td style={td}>{r.completedLast7}</td>
                </tr>
              ))}
              <tr style={{ background: colors.surfaceAlt }}>
                <th scope="row" style={{ ...td, textAlign: "left", padding: "9px 10px" }}>Total</th>
                <td style={{ ...td, fontWeight: 700, color: colors.danger }}>{taskTotals.overdue}</td>
                <td style={{ ...td, fontWeight: 700 }}>{taskTotals.dueNext7}</td>
                <td style={{ ...td, fontWeight: 700 }}>{taskTotals.completedLast7}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>{taskMode === "company" ? "Includes tasks the client owes as well as Sawtooth's. Click a company to see its tasks." : "Tasks assigned to each person. Click a name to see their tasks."}</div>
      </section>

      <section style={card}>
        <h2 style={h2}>Sawtooth Internal</h2>
        {(data.internal || []).length === 0 ? <div style={{ fontSize: 13, color: colors.textDim }}>No open internal work.</div> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 520, fontSize: 13, color: colors.text }}>
              <thead><tr>
                <th scope="col" style={{ ...th, textAlign: "left", padding: "8px 10px" }}>Objective</th>
                <th scope="col" style={{ ...th, textAlign: "left" }}>People</th>
                <th scope="col" style={th}>Open</th>
                <th scope="col" style={{ ...th, color: colors.success }}>Done</th>
                <th scope="col" style={{ ...th, color: colors.danger }}>Past due</th>
              </tr></thead>
              <tbody>
                {data.internal.map((r) => (
                  <tr key={r.objectiveId || "__none__"}>
                    <th scope="row" style={{ ...td, textAlign: "left", padding: "8px 10px" }}>
                      <button type="button" style={linkBtn}
                        onClick={() => onOpen?.(r.objectiveId ? `internal&objective=${encodeURIComponent(r.objectiveId)}` : "internal", "internal")}>{r.name}</button>
                    </th>
                    <td style={{ ...td, textAlign: "left", color: r.people.length ? colors.text : colors.textDim }}>{r.people.length ? r.people.join(", ") : "Unassigned"}</td>
                    <td style={td}>{r.open}</td>
                    <td style={td}>{r.done}</td>
                    <td style={{ ...td, fontWeight: 700, color: r.pastDue ? colors.danger : colors.textDim }}>{r.pastDue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>Internal objectives with open tasks. Click one to open it on the Sawtooth Internal page.</div>
      </section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 16 }}>
        {[["Upcoming projects", data.upcoming, "No upcoming projects.", (p) => ["Starts " + shortDate(p.startDate), "to " + longDate(p.endDate), colors.success]],
          ["Ending in the next 60 days", data.ending, "No projects ending in the next 60 days.", (p) => ["Ends " + shortDate(p.endDate), `${p.daysLeft} day${p.daysLeft === 1 ? "" : "s"} left`, colors.warning]]].map(([title, list, empty, meta]) => (
          <section key={title} style={card}>
            <h2 style={h2}>{title}</h2>
            {list.length === 0 && <div style={{ fontSize: 13, color: colors.textDim }}>{empty}</div>}
            {list.map((p) => { const [a, b, color] = meta(p); return (
              <div key={p.id} style={{ ...row, alignItems: "flex-start" }}>
                <div style={{ minWidth: 0 }}><button type="button" style={linkBtn} onClick={() => openClient(p.clientId, p.id)}>{p.name}</button>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>PM {uName(p.projectManager)} · Owner {uName(p.accountOwner)}</div></div>
                <div style={{ textAlign: "right", fontSize: 12.5, whiteSpace: "nowrap" }}><div style={{ color, fontWeight: 700 }}>{a}</div><div style={{ color: colors.textMuted }}>{b}</div></div>
              </div>
            ); })}
          </section>
        ))}
      </div>

      <section style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <h2 style={{ ...h2, margin: 0 }}>Team capacity · next 3 months</h2>
          <span style={{ fontSize: 12, color: colors.textMuted }}>Booked hours on projects each person manages vs their monthly hours available</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "110px repeat(3, minmax(170px, 1fr))", gap: "10px 16px", minWidth: 640, fontSize: 12.5, alignItems: "center", color: colors.text }}>
            <span />
            {data.capacity.months.map((m) => <span key={m} style={{ fontSize: 11, fontWeight: 700, color: colors.textDim, textTransform: "uppercase" }}>{MONTHS[Number(m.slice(5, 7)) - 1]} {m.slice(0, 4)}</span>)}
            {data.capacity.people.map((p) => (
              <React.Fragment key={p.userId}>
                <span style={{ fontWeight: 600 }}>{p.name}</span>
                {p.booked.map((raw, i) => {
                  const b = Math.round(raw); const a = p.available;
                  const over = a && b > a; const near = a && !over && b / a >= 0.9;
                  return (
                    <div key={i} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <span style={{ height: 10, background: colors.bg, borderRadius: 3, overflow: "hidden" }}><span style={{ display: "block", height: 10, width: a ? `${Math.min(100, Math.round((b / a) * 100))}%` : "0%", background: over ? colors.danger : near ? colors.warning : colors.accent }} /></span>
                      <span style={{ color: over ? colors.danger : colors.textMuted, fontVariantNumeric: "tabular-nums" }}>{a ? `${b} / ${a} h${over ? ` · over by ${b - a}` : ""}` : `${b} h booked`}</span>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {panel && (
        <ListPanel title={panel.title} onClose={() => setPanel(null)}>
          {panel.kind === "proposals" ? panel.items.map((p) => (
            <div key={p.id} style={{ ...row, display: "block" }}>
              <div style={{ fontWeight: 600, color: colors.text }}>{p.topic}</div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                {p.clientId ? <button type="button" style={{ ...linkBtn, fontSize: 12 }} onClick={() => { setPanel(null); openClient(p.clientId); }}>{p.client}</button> : p.client}
                {" · "}{stageLabel[p.stage]}{p.value ? ` · ${formatCurrency(p.value)}` : ""}{p.dueDate ? ` · due ${longDate(p.dueDate)}` : ""}
              </div>
            </div>
          )) : [["overdue", "Overdue"], ["dueNext7", "Due next 7 days"], ["completedLast7", "Completed last 7 days"]].map(([k, label]) => (
            <div key={k} style={{ marginBottom: 14 }}>
              <div style={{ ...h2, marginBottom: 4 }}>{label} ({panel.items[k].length})</div>
              {panel.items[k].length === 0 && <div style={{ fontSize: 12.5, color: colors.textDim }}>None.</div>}
              {panel.items[k].map((t) => (
                <div key={t.id} style={row}>
                  <button type="button" style={linkBtn} onClick={() => { setPanel(null); openTask(t); }}>{t.name}</button>
                  <span style={{ fontSize: 12, color: colors.textMuted, whiteSpace: "nowrap" }}>{t.client} · {k === "completedLast7" ? `done ${shortDate(t.completedAt)}` : `due ${shortDate(t.dueDate)}`}</span>
                </div>
              ))}
            </div>
          ))}
        </ListPanel>
      )}
    </div>
  );
}
